<template>
	<div>
		<ClientOnly>
			<component :is="consentComponent" />
			<MobileBanner v-if="showMobileAppBanner" />
			<TitleRemovalBanner />
			<VerifyEmailBanner v-if="showBanner" :userCreationTime="userCreationTime" />
			<ListsForSignedInUsersOnboarding v-if="!isLoggedIn" />
		</ClientOnly>
		<AdBlockerBait />
	</div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted } from 'vue';

import type { Consent } from '@/stores/useUserStoreTypes';

import { logTalentAd } from '@/helpers/ad/talent-ad';
import { TrackingHelper, initTrackers } from '@/helpers/tracking/tracking-helper';

import { isSameWeek } from '@/helpers/date-helper';
import { updateUserProfile } from '@/helpers/id-helper';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import TitleRemovalBanner from '@/components/banners/TitleRemovalBanner.vue';
import AdBlockerBait from './AdBlockerBait.vue';
import { lowPriorityCallback } from '@/helpers/request-idle-helper';
import { useVerifyEmail } from '@/helpers/composables/useVerifyEmail';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { useSentryTags } from '@/libraries/sentry/composables/useSentryTags';

const ConsentBanner = defineAsyncComponent(
	() => import(/* webpackChunkName: "consent-banner" */ '@/components/consent/ConsentBanner.vue')
);
const ListsForSignedInUsersOnboarding = defineAsyncComponent(
	() => import(/* webpackChunkName: "consent-banner" */ '@/components/modals/ListsForSignedInUsersOnboarding.vue')
);
const ConsentOverlay = defineAsyncComponent(
	() => import(/* webpackChunkName: "consent-overlay" */ '@/components/consent/ConsentOverlay.vue')
);

const MobileBanner = defineAsyncComponent(
	() => import(/* webpackChunkName: "mobile-banner" */ '@/components/MobileBanner.vue')
);
const VerifyEmailBanner = defineAsyncComponent(
	() => import(/* webpackChunkName: "verify-email-banner" */ '@/components/modals/account/VerifyEmailBanner.vue')
);

const { country, language, webLocale } = storeToRefs(useLanguageStore());

const userStore = useUserStore();
const { saveSettings, updateUserVisitStatus } = userStore;
const { isLoggedIn, jwId, lastTimeMobileAppBannerWasClosedTS } = storeToRefs(userStore);
const { isArbitrating, arbitration } = useArbitrage();
const { setSessionEnvironment } = useTrackingStore();
const { xServeTrack, isSeoUser } = storeToRefs(useAppStore());
const { showBanner, userCreationTime } = useVerifyEmail();
useSentryTags();

const consentComponent = computed(() => {
	if (hasUCConsentBanner(webLocale.value)) {
		return ConsentOverlay;
	}

	return ConsentBanner;
});

const showMobileAppBanner = computed(() => {
	return (
		!isLoggedIn.value &&
		!isSameWeek(new Date(lastTimeMobileAppBannerWasClosedTS.value), new Date()) &&
		!isArbitrating.value
	);
});

onMounted(() => {
	if (process.env.NODE_ENV !== 'development') {
		logTalentAd();
	}

	setSnowplowSessionEnvironmentContext();
	lowPriorityCallback(() => initTrackers());
	updateUserVisitStatus();

	// NOTE This is only for testing (otherwise we have no way to force old consents)
	(window as any).saveSettings = async (consents: Record<Consent, boolean>) => {
		await saveSettings({ jw_consents: consents });
	};

	if (country.value == null) return;

	try {
		updateUserProfile(jwId.value, country.value, language.value);
	} catch (error: any) {
		const label = error instanceof Error ? error.message : String(error);
		TrackingHelper.trackEvent('app_error', { action: 'update_user_profile_failure', label });
	}
});

function setSnowplowSessionEnvironmentContext() {
	if (import.meta.server) {
		return;
	}
	const referrer = document ? document.referrer : null;
	const url = window ? window.location.href : null;

	const params = getQueryParams(url);
	const origin = getOrigin(referrer);

	setSessionEnvironment({
		origin,
		originPageUrl: url,
		originPageReferred: referrer,
		originRefrMedium: arbitration.value?.utm_medium || params.medium,
		originRefrSource: arbitration.value?.utm_source || params.source,
		originRefrSocial: origin === 'social' ? getSocialPlatform(referrer) : null,
		env: xServeTrack.value,
	});
}

function getQueryParams(url: string | null) {
	if (!url) {
		return { medium: null, source: null };
	}
	const params = new URL(url).searchParams;
	return {
		medium: params.get('utm_medium') || null,
		source: params.get('utm_source') || null,
	};
}

function getOrigin(referrer: string | null) {
	if (isArbitrating.value) {
		return 'search';
	} else if (isSeoUser.value) {
		return 'seo';
	} else if (isSocial(referrer)) {
		return 'social';
	} else if (!referrer) {
		return 'direct';
	}

	// no paid search, seoUser false, no social, has refferer - assume returning
	return 'returning';
}

function isSocial(referrer: string | null) {
	if (!referrer) return false;

	const socialRegex =
		/(?:https?:\/\/)?(?:\w+\.)?(t\.co|facebook\.com|x\.com|twitter\.com|instagram\.com|youtube\.com|tiktok\.com|snapchat\.com|reddit\.com|bsky\.app|linkedin\.com)/i;
	return socialRegex.test(referrer);
}

function getSocialPlatform(referrer: string | null): string | null {
	if (!referrer) return null;

	const socialRegex =
		/(?:https?:\/\/)?(?:\w+\.)?(t\.co|facebook\.com|x\.com|twitter\.com|instagram\.com|youtube\.com|tiktok\.com|snapchat\.com|reddit\.com|bsky\.app|linkedin\.com)/i;

	const match = referrer.match(socialRegex);
	return match?.[1] ? match[1].replace(/\.com$/, '') : null;
}
</script>
